import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { generateOrderInvoiceLabel, getInPackingOrders } from '../../services/labelInvoice';
import { BiCopy } from 'react-icons/bi';
import { GoEye } from 'react-icons/go';
import ShowModal from '../../utils/ShowModal';
import OrderOrdersDetailsPopView from './OrderOrdersDetailsPopView';
import DateRangePicker from '../../components/inputFields/DateRangePicker';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100];
const LABEL_TYPES = ['prepaid', 'cod'];
const TAX_MODES = [
    { value: 'tax', label: 'Tax' },
    { value: 'no_tax', label: 'No Tax' },
];

const getFirstDayOfMonth = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
};

const getToday = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const Spinner = ({ className = 'h-4 w-4 border-white' }) => (
    <div className={`animate-spin rounded-full border-b-2 ${className}`}></div>
);

const normalizeOrders = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.results?.records)) return data.results.records;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.orders)) return data.orders;
    return [];
};

const getTotalCount = (data, fallback) => (
    data?.count ??
    data?.total ??
    data?.total_entries ??
    data?.results?.count ??
    fallback
);

const getOrderId = (order) => order?.id || order?.order_id || order?.uuid || order?.order_uuid || '';

const getCustomerName = (order) => (
    order?.name ||
    order?.user ||
    order?.customer_name ||
    order?.shipping_user?.name ||
    order?.shipping_user?.customer_name ||
    '-'
);

const getShippingAddress = (order) => {
    const shipping = order?.shipping_user || order?.shipping_address || {};
    if (typeof shipping === 'string') return shipping;
    return (
        shipping?.shipping_address ||
        shipping?.address ||
        shipping?.line1 ||
        order?.shipping_address_text ||
        order?.customer_address ||
        ''
    );
};

const getDueAmount = (order) => (
    order?.remaining_payment ??
    order?.due_amount ??
    order?.amount_due ??
    order?.payment_due ??
    order?.balance_amount ??
    order?.total_due ??
    ''
);

const getDefaultLabelType = (order) => (
    Number(getDueAmount(order)) > 0 ? 'cod' : 'prepaid'
);

const getPayloadCodAmount = (payload) => (
    payload?.label_type === 'cod' ? Number(payload?.amount) || 0 : 0
);

const formatCurrency = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '-';
    return `Rs. ${number.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const getCreatedDate = (order) => {
    const raw = order?.created_at || order?.order_date || order?.date;
    if (!raw) return '-';
    return String(raw).split('T')[0];
};

const getOrderItemsList = (order) => {
    if (Array.isArray(order?.order_items)) return order.order_items;
    if (Array.isArray(order?.items)) return order.items;
    return [];
};

const getOrderItemName = (item) => (
    item?.shop_product_aka_name ||
    item?.aka_name ||
    item?.product_aka_name ||
    item?.product_name ||
    item?.name ||
    item?.product ||
    ''
);

const makePayload = (order) => ({
    payload_id: `${getOrderId(order)}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    order_id: String(getOrderId(order)),
    label_type: getDefaultLabelType(order),
    amount: getDueAmount(order) || '',
    customer_address: getShippingAddress(order),
    tax_mode: 'no_tax',
    note: '',
});

function InPackingOrders() {
    const [ordersData, setOrdersData] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [searchText, setSearchText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState(getFirstDayOfMonth());
    const [endDate, setEndDate] = useState(getToday());
    const [printPayloads, setPrintPayloads] = useState([]);
    const [detailOrder, setDetailOrder] = useState({});
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(searchText.trim());
            setPage(1);
        }, 350);

        return () => clearTimeout(timeout);
    }, [searchText]);

    const fetchOrders = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        setLoading(true);
        setError('');

        try {
            const params = { page, page_size: pageSize };
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (debouncedSearch) params.search = debouncedSearch;

            const response = await getInPackingOrders(params, {
                signal: abortControllerRef.current.signal,
            });
            const data = response.data;
            const normalized = normalizeOrders(data);
            setOrdersData(data);
            setOrders(normalized);
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            console.error('In packing orders fetch error:', err);
            const message = err?.response?.data?.message || err?.response?.data?.error || 'Failed to fetch in-packing orders.';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, endDate, page, pageSize, startDate]);

    const handleRangeChange = ({ start, end }) => {
        if (start && end) {
            if (start > end) return;
            setStartDate(start);
            setEndDate(end);
            setPage(1);
            return;
        }
        if (!start && !end) {
            const s = getFirstDayOfMonth();
            const e = getToday();
            setStartDate(s);
            setEndDate(e);
            setPage(1);
        }
    };

    useEffect(() => {
        fetchOrders();
        return () => {
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, [fetchOrders]);

    const totalEntries = getTotalCount(ordersData, orders.length);
    const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));

    const selectedOrderIds = useMemo(() => (
        new Set(printPayloads.map((payload) => String(payload.order_id)))
    ), [printPayloads]);

    const payloadsByOrder = useMemo(() => (
        printPayloads.reduce((acc, payload) => {
            if (!acc[payload.order_id]) acc[payload.order_id] = [];
            acc[payload.order_id].push(payload);
            return acc;
        }, {})
    ), [printPayloads]);

    const orderDueById = useMemo(() => (
        orders.reduce((acc, order) => {
            const orderId = String(getOrderId(order));
            if (orderId) acc[orderId] = Number(getDueAmount(order)) || 0;
            return acc;
        }, {})
    ), [orders]);

    const getCodTotalForOrder = (payloads, orderId) => (
        payloads
            .filter((payload) => String(payload.order_id) === String(orderId))
            .reduce((sum, payload) => sum + getPayloadCodAmount(payload), 0)
    );

    const handleToggleOrder = (order) => {
        const orderId = String(getOrderId(order));
        if (!orderId) {
            toast.error('Order ID is missing for this row.');
            return;
        }

        setPrintPayloads((prev) => {
            if (prev.some((payload) => String(payload.order_id) === orderId)) {
                return prev.filter((payload) => String(payload.order_id) !== orderId);
            }
            return [...prev, makePayload(order)];
        });
    };

    const handleDuplicateParcel = (order) => {
        const orderId = String(getOrderId(order));
        if (!orderId) return;
        setPrintPayloads((prev) => {
            const payload = makePayload(order);
            if (payload.label_type === 'cod') {
                const dueAmount = Number(getDueAmount(order)) || 0;
                const currentCodTotal = getCodTotalForOrder(prev, orderId);
                const remainingCodAmount = Math.max(0, dueAmount - currentCodTotal);
                payload.amount = remainingCodAmount > 0 ? remainingCodAmount : '';
            }
            return [...prev, payload];
        });
    };

    const handlePayloadChange = (payloadId, field, value) => {
        setPrintPayloads((prev) => {
            const targetPayload = prev.find((payload) => payload.payload_id === payloadId);
            if (!targetPayload) return prev;

            const nextPayload = { ...targetPayload, [field]: value };
            const nextPayloads = prev.map((payload) => (
                payload.payload_id === payloadId ? nextPayload : payload
            ));
            const dueAmount = orderDueById[String(nextPayload.order_id)] ?? 0;
            const codTotal = getCodTotalForOrder(nextPayloads, nextPayload.order_id);

            if (nextPayload.label_type === 'cod' && codTotal > dueAmount) {
                toast.error(`COD amount total cannot exceed due amount ${formatCurrency(dueAmount)}.`);
                return prev;
            }

            return nextPayloads;
        });
    };

    const handleRemovePayload = (payloadId) => {
        setPrintPayloads((prev) => prev.filter((payload) => payload.payload_id !== payloadId));
    };

    const copyClipboard = (text) => {
        navigator.clipboard.writeText(String(text || ''))
            .then(() => toast.success('Copied to clipboard!'))
            .catch(() => toast.error('Failed to copy'));
    };

    const openDetailsModal = (order) => {
        setDetailOrder(order);
        setDetailModalOpen(true);
    };

    const closeDetailsModal = () => {
        setDetailModalOpen(false);
        setDetailOrder({});
    };

    const renderOrderCopyButton = (orderId) => (
        orderId ? (
            <button
                type="button"
                onClick={() => copyClipboard(orderId)}
                className="inline-flex items-center justify-center rounded-lg bg-blue-50 p-2 text-blue-600 transition-colors hover:bg-blue-100"
                title="Copy order ID"
                aria-label="Copy order ID"
            >
                <BiCopy size={18} />
            </button>
        ) : (
            <span className="text-xs text-gray-400">-</span>
        )
    );

    const renderOrderProducts = (order) => {
        const items = getOrderItemsList(order);
        if (!items.length) return <p className="text-xs text-gray-400">-</p>;

        const visible = items.slice(0, 3);
        const remaining = items.length - visible.length;

        return (
            <div className="max-w-[11rem] space-y-0.5">
                {visible.map((item, index) => {
                    const name = getOrderItemName(item);
                    const quantity = item?.quantity ?? item?.qty ?? 1;
                    return (
                        <p key={item?.id || index} className="truncate text-xs text-gray-800" title={name}>
                            {name || '-'} <span className="text-gray-400">x{quantity}</span>
                        </p>
                    );
                })}
                {remaining > 0 && (
                    <p className="text-[10px] font-medium text-gray-500">+{remaining} more</p>
                )}
            </div>
        );
    };

    const handleGeneratePdf = async () => {
        if (printPayloads.length === 0) {
            toast.error('Select at least one order to generate a PDF.');
            return;
        }

        const invalidPayload = printPayloads.find((payload) => (
            !String(payload.order_id).trim() ||
            !String(payload.customer_address).trim() ||
            (payload.label_type === 'cod' && Number(payload.amount) <= 0)
        ));

        if (invalidPayload) {
            toast.error('Please complete order ID, address, and COD amount before generating.');
            return;
        }

        const missingNotePayload = printPayloads.find((payload) => (
            (payloadsByOrder[payload.order_id] || []).length > 1 &&
            !String(payload.note || '').trim()
        ));

        if (missingNotePayload) {
            toast.error('Please add a note for each duplicated parcel before generating.');
            return;
        }

        const exceededOrderId = Object.keys(payloadsByOrder).find((orderId) => {
            const dueAmount = orderDueById[String(orderId)] ?? 0;
            return getCodTotalForOrder(printPayloads, orderId) > dueAmount;
        });

        if (exceededOrderId) {
            toast.error(`COD amount total cannot exceed due amount ${formatCurrency(orderDueById[exceededOrderId])}.`);
            return;
        }

        setGenerating(true);
        setError('');

        try {
            const response = await generateOrderInvoiceLabel({
                orders: printPayloads.map((payload) => ({
                    order_id: String(payload.order_id).trim(),
                    label_type: payload.label_type,
                    amount: Number(payload.amount) || 0,
                    customer_address: String(payload.customer_address).trim(),
                    tax_mode: payload.tax_mode || 'tax',
                    note: String(payload.note || '').trim(),
                })),
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            toast.success('PDF generated successfully.');
        } catch (err) {
            console.error('Order invoice label generation error:', err);
            const message = err?.response?.data?.message || err?.response?.data?.error || 'Failed to generate PDF.';
            setError(message);
            toast.error(message);
        } finally {
            setGenerating(false);
        }
    };

    const handlePageSizeChange = (value) => {
        setPageSize(Number(value));
        setPage(1);
    };

    return (
        <div className="mt-5 space-y-5">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">B2C Labels</h2>
                        <p className="mt-1 text-xs text-gray-500">
                            Select orders, edit label details in the table, duplicate parcels, and generate one PDF.
                        </p>
                    </div>
                    <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                        <DateRangePicker
                            compact
                            value={{ start: startDate, end: endDate }}
                            onChange={handleRangeChange}
                            placeholder="Choose start & end date"
                        />
                        <input
                            type="search"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            placeholder="Search order, customer, phone..."
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-72"
                        />
                        <button
                            type="button"
                            onClick={fetchOrders}
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
                        >
                            {loading ? <Spinner className="h-4 w-4 border-gray-500" /> : null}
                            Refresh
                        </button>
                        <button
                            type="button"
                            onClick={handleGeneratePdf}
                            disabled={generating || printPayloads.length === 0}
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {generating ? <Spinner /> : null}
                            Generate PDF
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-[1380px] text-left text-sm">
                        <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="w-12 px-4 py-3">Print</th>
                                <th className="px-4 py-3">Order</th>
                                <th className="px-4 py-3">Customer</th>
                                <th className="px-4 py-3">Products</th>
                                <th className="px-4 py-3">Label Type</th>
                                <th className="px-4 py-3">Tax Mode</th>
                                <th className="px-4 py-3">COD Amount</th>
                                <th className="px-4 py-3">Customer Address</th>
                                <th className="px-4 py-3">Note</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading && (
                                <tr>
                                    <td colSpan={10} className="px-4 py-10 text-center text-gray-500">
                                        <span className="inline-flex items-center gap-2">
                                            <Spinner className="h-5 w-5 border-blue-500" />
                                            Loading in-packing orders...
                                        </span>
                                    </td>
                                </tr>
                            )}

                            {!loading && orders.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-4 py-10 text-center text-gray-400">
                                        No in-packing orders found.
                                    </td>
                                </tr>
                            )}

                            {!loading && orders.map((order, index) => {
                                const orderId = String(getOrderId(order));
                                const selected = selectedOrderIds.has(orderId);
                                const selectedPayloads = payloadsByOrder[orderId] || [];

                                if (!selected) {
                                    return (
                                        <tr key={orderId || index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 align-top">
                                                <input
                                                    type="checkbox"
                                                    checked={false}
                                                    onChange={() => handleToggleOrder(order)}
                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    aria-label={`Select order ${orderId}`}
                                                />
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                <div className="max-w-[190px]">
                                                    {renderOrderCopyButton(orderId)}
                                                    <p className="mt-1 text-xs text-gray-500">{getCreatedDate(order)}</p>
                                                    <span className="mt-2 inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                                                        {order?.status || 'IN_PACKAGING'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                <p className="font-medium text-gray-800">{getCustomerName(order)}</p>
                                                <p className="mt-1 text-xs text-gray-500">{order?.phone_number || order?.mobile_number || '-'}</p>
                                                <p className="mt-1 text-xs text-gray-400">{order?.platform || order?.shop_name || ''}</p>
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                {renderOrderProducts(order)}
                                            </td>
                                            <td className="px-4 py-3 align-top text-xs text-gray-400">Select to edit</td>
                                            <td className="px-4 py-3 align-top text-xs text-gray-400">Select to edit</td>
                                            <td className="px-4 py-3 align-top">
                                                <p className="font-medium text-gray-900">{formatCurrency(order?.total_price)}</p>
                                                <p className="mt-1 text-xs text-red-600">Due: {formatCurrency(getDueAmount(order))}</p>
                                                <p className="mt-1 text-xs text-gray-500">{order?.payment_status || '-'}</p>
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                <p className="line-clamp-3 max-w-xs text-xs leading-5 text-gray-600" title={getShippingAddress(order)}>
                                                    {getShippingAddress(order) || '-'}
                                                </p>
                                            </td>
                                            <td className="px-4 py-3 align-top text-xs text-gray-400">-</td>
                                            <td className="px-4 py-3 text-right align-top">
                                                <div className="flex items-center justify-end gap-2">
                                                    <GoEye
                                                        size={20}
                                                        className="cursor-pointer text-purple-600"
                                                        title="View order details"
                                                        onClick={() => openDetailsModal(order)}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleOrder(order)}
                                                        className="rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }

                                return selectedPayloads.map((payload, payloadIndex) => (
                                    <tr key={payload.payload_id} className="bg-blue-50/30 hover:bg-blue-50/60">
                                        <td className="px-4 py-3 align-top">
                                            {payloadIndex === 0 ? (
                                                <input
                                                    type="checkbox"
                                                    checked
                                                    onChange={() => handleToggleOrder(order)}
                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    aria-label={`Unselect order ${orderId}`}
                                                />
                                            ) : (
                                                <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                                                    Copy
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <div className="max-w-[190px]">
                                                {renderOrderCopyButton(payload.order_id)}
                                                <p className="mt-1 text-xs text-gray-500">{getCreatedDate(order)}</p>
                                                <span className="mt-2 inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                                                    Parcel {payloadIndex + 1}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <p className="font-medium text-gray-800">{getCustomerName(order)}</p>
                                            <p className="mt-1 text-xs text-gray-500">{order?.phone_number || order?.mobile_number || '-'}</p>
                                            <p className="mt-1 text-xs text-gray-400">{order?.platform || order?.shop_name || ''}</p>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            {renderOrderProducts(order)}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <select
                                                value={payload.label_type}
                                                onChange={(event) => handlePayloadChange(payload.payload_id, 'label_type', event.target.value)}
                                                className="w-32 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm capitalize focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                            >
                                                {LABEL_TYPES.map((type) => (
                                                    <option key={type} value={type}>{type}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <select
                                                value={payload.tax_mode || 'tax'}
                                                onChange={(event) => handlePayloadChange(payload.payload_id, 'tax_mode', event.target.value)}
                                                className="w-32 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                            >
                                                {TAX_MODES.map((mode) => (
                                                    <option key={mode.value} value={mode.value}>{mode.label}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            {payload.label_type === 'cod' ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={payload.amount}
                                                        onChange={(event) => handlePayloadChange(payload.payload_id, 'amount', event.target.value)}
                                                        className="w-28 rounded-lg border border-gray-300 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                    />
                                                    <p className="mt-1 text-xs text-gray-500">COD amount</p>
                                                </>
                                            ) : (
                                                <span className="text-xs text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <textarea
                                                rows={3}
                                                value={payload.customer_address}
                                                onChange={(event) => handlePayloadChange(payload.payload_id, 'customer_address', event.target.value)}
                                                className="w-80 resize-none rounded-lg border border-gray-300 px-3 py-2 text-xs leading-5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                            />
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <input
                                                type="text"
                                                value={payload.note || ''}
                                                onChange={(event) => handlePayloadChange(payload.payload_id, 'note', event.target.value)}
                                                placeholder={selectedPayloads.length > 1 ? 'Note (required)' : 'Note (optional)'}
                                                className="w-40 rounded-lg border border-gray-300 px-2 py-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-right align-top">
                                            <div className="flex flex-col items-end gap-2">
                                                <GoEye
                                                    size={20}
                                                    className="cursor-pointer text-purple-600"
                                                    title="View order details"
                                                    onClick={() => openDetailsModal(order)}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleDuplicateParcel(order)}
                                                    className="rounded-md bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-200"
                                                >
                                                    Duplicate Parcel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemovePayload(payload.payload_id)}
                                                    className="rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-100"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ));
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>Show</span>
                        <select
                            value={pageSize}
                            onChange={(event) => handlePageSizeChange(event.target.value)}
                            className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                        <span>per page</span>
                    </div>

                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                        <span className="text-sm text-gray-500">
                            {totalEntries} total | Page {page} of {totalPages} | {printPayloads.length} parcel payloads selected
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                disabled={page <= 1 || loading}
                                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                disabled={page >= totalPages || loading}
                                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ShowModal
                contentLabel="Order Details"
                isOpen={detailModalOpen}
                modalWidth="50%"
                isSidebar={true}
                onRequestClose={closeDetailsModal}
                componentToPass={<OrderOrdersDetailsPopView item={detailOrder} onListRefresh={fetchOrders} />}
            />
        </div>
    );
}

export default InPackingOrders;
